import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import { Switch } from "@shared/ui/switch";
import { Check, Loader2 } from "lucide-react";
import { useUpdateWidgets, useGetWidgets } from "../../hooks";
import { useRemoteUtilityId } from '@shared/selectors/globalSelectors';

const Widgets = () => {
  const [isPending, setIsPending] = useState(false);
  const remoteUtilityId=useRemoteUtilityId();
  const { mutate: updateWidgets } = useUpdateWidgets();
  
  const { data: widgetsResponse, refetch: refetchWidgets } = useGetWidgets({ remote_utility_id: remoteUtilityId });

  // Widget Toggles State
  const [showUsageChart, setShowUsageChart] = useState(true);
  const [showBillTrend, setShowBillTrend] = useState(true);
  const [showRecentBills, setShowRecentBills] = useState(true);
  const [showRecentRequests, setShowRecentRequests] = useState(true);

  useEffect(() => {
    if (widgetsResponse?.result) {
      const { usageChart, billTrendChart, recentBills, recentRequests } = widgetsResponse.result;
      setShowUsageChart(usageChart ?? true);
      setShowBillTrend(billTrendChart ?? true);
      setShowRecentBills(recentBills ?? true);
      setShowRecentRequests(recentRequests ?? true);
    }
  }, [widgetsResponse]);

  const handleReset = () => {
    setShowUsageChart(true);
    setShowBillTrend(true);
    setShowRecentBills(true);
    setShowRecentRequests(true);
  };

  const handleSave = () => {
    setIsPending(true);
    const payload = {
      remote_utility_id: remoteUtilityId,
      usage_chart: showUsageChart,
      bill_trend_chart: showBillTrend,
      recent_bills: showRecentBills,
      recent_requests: showRecentRequests
    };

    updateWidgets(payload, {
      onSuccess: () => {
        refetchWidgets();
      },
      onSettled: () => {
        setIsPending(false);
      }
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Settings Column */}
      <Card className="shadow-sm border-gray-200">
        <CardContent className="">
          <h2 className="text-lg font-bold text-gray-900 mb-1">Dashboard Widgets</h2>
          <p className="text-sm text-gray-500 mb-8">
            Control which widgets are visible on the customer dashboard. Disabled widgets are hidden from all end-users immediately after saving.
          </p>

          <div className="space-y-8">
            {/* USAGE & BILLING */}
            <div>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">USAGE & BILLING</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between border border-gray-100 rounded-lg p-4 bg-gray-50/50">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Usage Chart</h4>
                    <p className="text-xs text-gray-500">Displays the customer's daily or monthly consumption trend as a bar chart.</p>
                  </div>
                  <Switch checked={showUsageChart} onCheckedChange={setShowUsageChart} />
                </div>
                
                <div className="flex items-center justify-between border border-gray-100 rounded-lg p-4 bg-gray-50/50">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Bill Trend Chart</h4>
                    <p className="text-xs text-gray-500">Shows a line graph of bill amounts over the past 12 months.</p>
                  </div>
                  <Switch checked={showBillTrend} onCheckedChange={setShowBillTrend} />
                </div>
              </div>
            </div>

            {/* ACTIVITY */}
            <div>
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">ACTIVITY</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between border border-gray-100 rounded-lg p-4 bg-gray-50/50">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Recent Bills</h4>
                    <p className="text-xs text-gray-500">Lists the customer's last 5 bills with amount, due date, and payment status.</p>
                  </div>
                  <Switch checked={showRecentBills} onCheckedChange={setShowRecentBills} />
                </div>

                <div className="flex items-center justify-between border border-gray-100 rounded-lg p-4 bg-gray-50/50">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">Recent Requests</h4>
                    <p className="text-xs text-gray-500">Shows the latest service requests submitted by the customer with their current status.</p>
                  </div>
                  <Switch checked={showRecentRequests} onCheckedChange={setShowRecentRequests} />
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-100 mt-8">
            <Button variant="ghost" className="text-gray-600 font-semibold" onClick={handleReset} disabled={isPending}>
              Reset to Default
            </Button>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white gap-2" onClick={handleSave} disabled={isPending}>
              {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Save Widget Settings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview Column */}
      <Card className="shadow-sm border-gray-200">
        <CardContent className="">
          <h2 className="text-lg font-bold text-gray-900 mb-1">Preview</h2>
          <p className="text-sm text-gray-500 mb-6">A simplified representation of the customer dashboard showing only the enabled widgets.</p>
          
          <div className="w-full bg-[#f4f7fb] border border-gray-200 rounded-lg overflow-hidden flex flex-col relative min-h-[500px]">
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest px-4 py-3 border-b border-gray-200 bg-gray-100/80 shrink-0">
              CUSTOMER DASHBOARD PREVIEW
            </div>
            
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 auto-rows-min items-start">
              {showUsageChart && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 flex flex-col h-[180px]">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="text-xs font-bold text-gray-900">Water Consumption</h4>
                      <p className="text-[10px] text-gray-500">Last 6 months (CCF)</p>
                    </div>
                    <span className="text-[10px] text-blue-600 cursor-pointer">Details →</span>
                  </div>
                  <div className="flex-1 flex items-end justify-between px-2 pb-1 relative">
                    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 50" preserveAspectRatio="none">
                      <path d="M0,35 L25,32 L50,15 L75,30 L100,40" fill="none" stroke="#6366f1" strokeWidth="2" vectorEffect="non-scaling-stroke" />
                      <circle cx="0" cy="35" r="3" fill="#6366f1" />
                      <circle cx="25" cy="32" r="3" fill="#6366f1" />
                      <circle cx="50" cy="15" r="3" fill="#6366f1" />
                      <circle cx="75" cy="30" r="3" fill="#6366f1" />
                      <circle cx="100" cy="40" r="3" fill="#6366f1" />
                    </svg>
                  </div>
                  <div className="flex justify-between mt-2 px-1 text-[8px] text-gray-400 font-medium">
                    <span>May</span>
                    <span>Jun</span>
                    <span>Jul</span>
                    <span>Aug</span>
                    <span>Sep</span>
                    <span>Oct</span>
                  </div>
                </div>
              )}

              {showBillTrend && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 flex flex-col h-[180px]">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="text-xs font-bold text-gray-900">Billing Trend</h4>
                      <p className="text-[10px] text-gray-500">Monthly bill amount ($)</p>
                    </div>
                    <span className="text-[10px] text-blue-600 cursor-pointer">View bills →</span>
                  </div>
                  <div className="flex-1 flex items-end justify-between gap-1.5 px-2">
                    <div className="w-full bg-[#818cf8] rounded-t-sm" style={{ height: '40%' }}></div>
                    <div className="w-full bg-[#818cf8] rounded-t-sm" style={{ height: '35%' }}></div>
                    <div className="w-full bg-[#818cf8] rounded-t-sm" style={{ height: '70%' }}></div>
                    <div className="w-full bg-[#818cf8] rounded-t-sm" style={{ height: '60%' }}></div>
                    <div className="w-full bg-[#818cf8] rounded-t-sm" style={{ height: '30%' }}></div>
                  </div>
                  <div className="flex justify-between mt-2 px-1 text-[8px] text-gray-400 font-medium">
                    <span>May</span>
                    <span>Jun</span>
                    <span>Jul</span>
                    <span>Aug</span>
                    <span>Sep</span>
                  </div>
                </div>
              )}

              {showRecentBills && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 h-[180px] flex flex-col">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-xs font-bold text-gray-900">Recent Bills</h4>
                    <span className="text-[10px] text-blue-600 cursor-pointer">View all</span>
                  </div>
                  <div className="flex-1 overflow-hidden space-y-2.5">
                    <div className="flex justify-between items-center border-b border-gray-50 pb-1.5">
                      <div>
                        <div className="text-[10px] font-semibold text-gray-800">BILL-2024-0489</div>
                        <div className="text-[8px] text-gray-500">Nov 20, 2024</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] font-bold">$86.07</div>
                        <div className="text-[8px] text-red-500 font-semibold">Unpaid</div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center border-b border-gray-50 pb-1.5">
                      <div>
                        <div className="text-[10px] font-semibold text-gray-800">BILL-2024-0381</div>
                        <div className="text-[8px] text-gray-500">Oct 20, 2024</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] font-bold">$84.66</div>
                        <div className="text-[8px] text-red-500 font-semibold">Unpaid</div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center border-b border-gray-50 pb-1.5">
                      <div>
                        <div className="text-[10px] font-semibold text-gray-800">BILL-2024-0275</div>
                        <div className="text-[8px] text-gray-500">Sep 20, 2024</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] font-bold">$119.32</div>
                        <div className="text-[8px] text-green-500 font-semibold">Paid</div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center pb-1">
                      <div>
                        <div className="text-[10px] font-semibold text-gray-800">BILL-2024-0169</div>
                        <div className="text-[8px] text-gray-500">Aug 20, 2024</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] font-bold">$152.18</div>
                        <div className="text-[8px] text-green-500 font-semibold">Paid</div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 shrink-0 text-center text-[10px] font-semibold bg-[#ef4444] text-white py-1.5 rounded cursor-pointer">
                    Pay 2 Unpaid Bills — $170.73
                  </div>
                </div>
              )}

              {showRecentRequests && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 h-[180px] flex flex-col">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-xs font-bold text-gray-900">Recent Requests</h4>
                    <span className="text-[10px] text-blue-600 cursor-pointer">View all</span>
                  </div>
                  <div className="flex-1 overflow-hidden space-y-3.5">
                    <div className="flex justify-between items-center">
                      <div className="flex gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-1.5 shrink-0"></div>
                        <div>
                          <div className="text-[10px] font-semibold text-gray-800 flex items-center gap-1">Low Pressure <span className="text-[8px] bg-blue-50 text-blue-600 px-1 rounded">Complaint</span></div>
                          <div className="text-[8px] text-gray-500">CMP-2024-9841</div>
                        </div>
                      </div>
                      <div className="text-[8px] px-2 py-0.5 bg-orange-50 text-orange-600 font-semibold rounded border border-orange-100">In Progress</div>
                    </div>

                    <div className="flex justify-between items-center">
                      <div className="flex gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500 mt-1.5 shrink-0"></div>
                        <div>
                          <div className="text-[10px] font-semibold text-gray-800 flex items-center gap-1">Billing Issue <span className="text-[8px] bg-blue-50 text-blue-600 px-1 rounded">Complaint</span></div>
                          <div className="text-[8px] text-gray-500">CMP-2024-7720</div>
                        </div>
                      </div>
                      <div className="text-[8px] px-2 py-0.5 bg-green-50 text-green-600 font-semibold rounded border border-green-100">Resolved</div>
                    </div>

                    <div className="flex justify-between items-center">
                      <div className="flex gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5 shrink-0"></div>
                        <div>
                          <div className="text-[10px] font-semibold text-gray-800 flex items-center gap-1">Meter Issue <span className="text-[8px] bg-blue-50 text-blue-600 px-1 rounded">Complaint</span></div>
                          <div className="text-[8px] text-gray-500">CMP-2024-5530</div>
                        </div>
                      </div>
                      <div className="text-[8px] px-2 py-0.5 bg-gray-50 text-gray-600 font-semibold rounded border border-gray-200">Closed</div>
                    </div>

                    <div className="flex justify-between items-center">
                      <div className="flex gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0"></div>
                        <div>
                          <div className="text-[10px] font-semibold text-gray-800 flex items-center gap-1">Meter Replacement <span className="text-[8px] bg-green-50 text-green-600 px-1 rounded">Service Request</span></div>
                          <div className="text-[8px] text-gray-500">SRQ-2024-4471</div>
                        </div>
                      </div>
                      <div className="text-[8px] px-2 py-0.5 bg-blue-50 text-blue-600 font-semibold rounded border border-blue-100">Assigned</div>
                    </div>
                  </div>
                  
                  <div className="mt-2 shrink-0 border-t border-gray-100 pt-2 text-center">
                    <span className="text-[10px] text-gray-500 cursor-pointer flex items-center justify-center gap-1">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                      View All Requests
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Widgets;
